import type {
  UpdateCallOffMutationOptions,
  UpdateCallOffVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysCallOff } from '../queryKeys';

import { updateCallOff } from './updateCallOff';

/**
 * Custom hook for updating an existing call-off.
 * Updates call-off fields with status transition validation.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/update_calloff_calloffs__calloff_id__put
 */
export const useMutationUpdateCallOff = (
  options?: UpdateCallOffMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ callOffId, payload }: UpdateCallOffVariables) =>
      updateCallOff(callOffId, payload),
    onSuccess(_data, variables) {
      // Invalidate the specific call-off query
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.get(variables.callOffId),
      });
      // Invalidate the call-offs list
      queryClient.invalidateQueries({
        queryKey: queryKeysCallOff.getAll(undefined),
      });
    },
    ...options,
  });
};
